/**
 * The resolve-live refusal (piece 5, decision 0442). A refusal a person in
 * front of the screen is allowed to cure opens a modal that names the missing
 * thing, gathers it, writes it and re-runs the act that was refused. The
 * banner stays for a refusal the modal cannot cure: no permission, or a rule
 * the plan forbids crossing.
 *
 * This file is the sequencing alone, with no DOM in it (decision 0242). The
 * order is fixed and is the whole point: the cure is written first, and the
 * original act is re-run only once the write succeeded. Re-running first, or
 * running both at once, is how a person ends up with the prerequisite written
 * twice or an act that succeeds against a row that is not there yet.
 */

/** A write or an act, answered the way every screen's own writes answer. */
export type ResolveOutcome = { ok: true } | { ok: false; message: string };

/**
 * `gathering` is the person filling the form; `writing` is the cure going in;
 * `retrying` is the original act running again; `settled` is both done and the
 * modal closing. There is no `failed` step: a failure returns to `gathering`
 * with the reason beside the form, because the person is still the one who can
 * fix it and the typing they already did is still worth keeping.
 */
export const RESOLVE_LIVE_STEPS = ['gathering', 'writing', 'retrying', 'settled'] as const;
export type ResolveLiveStep = (typeof RESOLVE_LIVE_STEPS)[number];

export interface ResolveLiveState {
  step: ResolveLiveStep;
  /**
   * The refusal the person is looking at. It opens as the original refusal and
   * is replaced by whatever a failed cure or a failed retry said, so the modal
   * never shows a stale reason for a newer failure.
   */
  refusal: string;
  /** 🔨 True once the cure is written, so a retry that fails does not write it twice. */
  cured: boolean;
}

export type ResolveLiveEvent =
  | { type: 'submit' }
  | { type: 'cured' }
  | { type: 'retried' }
  | { type: 'failed'; message: string };

export function resolveLiveStart(refusal: string): ResolveLiveState {
  return { step: 'gathering', refusal, cured: false };
}

/** 🔨 The busy steps take no `submit`, so a second click cannot start a second write. */
export function resolveLiveBusy(state: ResolveLiveState): boolean {
  return state.step === 'writing' || state.step === 'retrying';
}

export function resolveLiveReducer(state: ResolveLiveState, event: ResolveLiveEvent): ResolveLiveState {
  switch (event.type) {
    case 'submit':
      if (resolveLiveBusy(state) || state.step === 'settled') return state;
      // A cure already written is not written again: the submit goes straight
      // back to the act that was refused.
      return { ...state, step: state.cured ? 'retrying' : 'writing' };
    case 'cured':
      if (state.step !== 'writing') return state;
      return { ...state, step: 'retrying', cured: true };
    case 'retried':
      if (state.step !== 'retrying') return state;
      return { ...state, step: 'settled' };
    case 'failed':
      if (state.step === 'settled') return state;
      return { ...state, step: 'gathering', refusal: event.message };
    default:
      return state;
  }
}

/**
 * The whole sequence, for a caller that has a cure and a retry to hand. It
 * returns the last state, so the caller closes on `settled` and stays open on
 * `gathering` with the reason the state carries.
 */
export async function runResolveLive(
  state: ResolveLiveState,
  cure: () => Promise<ResolveOutcome>,
  retry: () => Promise<ResolveOutcome>,
  onStep: (next: ResolveLiveState) => void = () => undefined,
): Promise<ResolveLiveState> {
  let current = resolveLiveReducer(state, { type: 'submit' });
  if (current === state) return state;
  onStep(current);

  if (current.step === 'writing') {
    const written = await cure();
    current = resolveLiveReducer(current, written.ok ? { type: 'cured' } : { type: 'failed', message: written.message });
    onStep(current);
    if (current.step !== 'retrying') return current;
  }

  const rerun = await retry();
  current = resolveLiveReducer(current, rerun.ok ? { type: 'retried' } : { type: 'failed', message: rerun.message });
  onStep(current);
  return current;
}
